import { BaseExternalBankAccount, baseExternalBankAccountToJson } from '@klear/bank-account/base'
import {
  EXTERNAL_BANK_ACCOUNT_VERIFICATION_STATUS_FAILED,
  EXTERNAL_BANK_ACCOUNT_VERIFICATION_STATUS_PENDING,
  EXTERNAL_BANK_ACCOUNT_VERIFICATION_STATUS_UNVERIFIED,
  EXTERNAL_BANK_ACCOUNT_VERIFICATION_STATUS_VERIFIED,
  type ExternalBankAccountVerificationStatus
} from '@klear/bank-account/constants'
import type { BankPaymentNetwork } from '@klear/bank-account/constants'
import { KlearDate } from '@klear/date'
import { Timestamp } from '@klear/time'

export function traderExternalBankAccountToJson(account: TraderExternalBankAccount) {
  return {
    ...baseExternalBankAccountToJson(account),
    stripeSetupIntentId: account.stripeSetupIntentId,
    stripePaymentMethodId: account.stripePaymentMethodId,
    verificationStatus: account.verificationStatus,
    verifiedAt: account.verifiedAt?.epochMs,
    verificationAttempts: account.verificationAttempts,
    traderLegalAgreementAcceptanceId: account.traderLegalAgreementAcceptanceId,
    achSignedDocumentKey: account.achSignedDocumentKey,
    microdepositArrivalAt: account.microdepositArrivalAt?.epochMs,
    detectedPaymentNetworks: account.detectedPaymentNetworks
  }
}
export type TraderExternalBankAccountJson = ReturnType<typeof traderExternalBankAccountToJson>

export class TraderExternalBankAccount extends BaseExternalBankAccount {
  static readonly __klearClass = 'TraderExternalBankAccount'

  stripeSetupIntentId?: string
  stripePaymentMethodId?: string
  verificationStatus: ExternalBankAccountVerificationStatus
  verifiedAt?: Timestamp
  verificationAttempts: number
  traderLegalAgreementAcceptanceId?: number
  achSignedDocumentKey?: string
  microdepositArrivalAt?: Timestamp
  detectedPaymentNetworks?: BankPaymentNetwork[]

  constructor(props: TraderExternalBankAccountJson) {
    super(props)
    this.stripeSetupIntentId = props.stripeSetupIntentId
    this.stripePaymentMethodId = props.stripePaymentMethodId
    this.verificationStatus = props.verificationStatus ?? EXTERNAL_BANK_ACCOUNT_VERIFICATION_STATUS_UNVERIFIED
    this.verifiedAt = props.verifiedAt != null ? new Timestamp(props.verifiedAt) : undefined
    this.verificationAttempts = props.verificationAttempts ?? 0
    this.traderLegalAgreementAcceptanceId = props.traderLegalAgreementAcceptanceId
    this.achSignedDocumentKey = props.achSignedDocumentKey
    this.microdepositArrivalAt =
      props.microdepositArrivalAt != null ? new Timestamp(props.microdepositArrivalAt) : undefined
    this.detectedPaymentNetworks = props.detectedPaymentNetworks
    this.assertValid()
  }

  assertValid(): void {
    if (!this.organizationId) {
      throw new Error('TraderExternalBankAccount organizationId is required')
    }
  }

  toJSON() {
    return traderExternalBankAccountToJson(this)
  }

  get isVerified(): boolean {
    return this.verificationStatus === EXTERNAL_BANK_ACCOUNT_VERIFICATION_STATUS_VERIFIED
  }

  get isWireOnly(): boolean {
    return this.isWireSupported && !this.isAchSupported
  }

  get requiresMicrodepositVerification(): boolean {
    return this.isAchSupported
  }

  get requiresAchAuthorization(): boolean {
    return this.isAchSupported
  }

  get hasAttachedPaymentMethod(): boolean {
    return !!this.stripePaymentMethodId
  }

  get isReadyForTransfer(): boolean {
    if (this.requiresMicrodepositVerification) return this.isVerified
    return this.isWireSupported
  }

  get isReadyForInboundTransfer(): boolean {
    return this.isAchSupported && this.isVerified && this.hasAttachedPaymentMethod
  }

  get transferBlockedReason(): string | undefined {
    if (this.isReadyForTransfer) return undefined
    if (this.requiresMicrodepositVerification && !this.isVerified) {
      return 'Verify this account with microdeposits before sending to it.'
    }
    return 'This account has no usable payment network.'
  }

  get needsVerification(): boolean {
    if (!this.requiresMicrodepositVerification) return false
    return (
      this.verificationStatus === EXTERNAL_BANK_ACCOUNT_VERIFICATION_STATUS_PENDING ||
      this.verificationStatus === EXTERNAL_BANK_ACCOUNT_VERIFICATION_STATUS_UNVERIFIED
    )
  }

  get canVerify(): boolean {
    if (!this.requiresMicrodepositVerification) return false
    return this.verificationStatus === EXTERNAL_BANK_ACCOUNT_VERIFICATION_STATUS_PENDING && !!this.stripeSetupIntentId
  }

  getRemainingAttempts(verificationMethod: 'amounts' | 'descriptor_code' | null): number {
    const maxAttempts = verificationMethod === 'descriptor_code' ? 10 : 3
    return Math.max(0, maxAttempts - this.verificationAttempts)
  }

  get verificationStatusLabel(): string {
    switch (this.verificationStatus) {
      case EXTERNAL_BANK_ACCOUNT_VERIFICATION_STATUS_VERIFIED:
        if (this.requiresAchAuthorization && !this.traderLegalAgreementAcceptanceId) {
          return 'Verified Not Authorized'
        }
        return 'Verified'
      case EXTERNAL_BANK_ACCOUNT_VERIFICATION_STATUS_PENDING:
        return 'Pending Verification'
      case EXTERNAL_BANK_ACCOUNT_VERIFICATION_STATUS_FAILED:
        return 'Verification Failed'
      case EXTERNAL_BANK_ACCOUNT_VERIFICATION_STATUS_UNVERIFIED:
      default:
        return this.isWireOnly ? 'Wire only' : 'Unverified'
    }
  }

  get estimatedMicrodepositAvailabilityDate(): KlearDate | undefined {
    if (!this.requiresMicrodepositVerification) return undefined
    if (this.verificationStatus !== EXTERNAL_BANK_ACCOUNT_VERIFICATION_STATUS_PENDING) return undefined

    if (this.microdepositArrivalAt) {
      return this.microdepositArrivalAt.toKlearDate()
    }

    let cursor: KlearDate | undefined = this.createdAt.toKlearDate()
    if (cursor == null) return undefined
    let businessDaysAdded = 0
    while (businessDaysAdded < 2) {
      cursor = cursor.add(1, 'day')
      const dow = cursor.day()
      if (dow !== 0 && dow !== 6) businessDaysAdded++
    }
    return cursor
  }
}
